/**
 * What the agent has put on screen that the user still has to deal with.
 *
 * Both interactive cards — the questions batch and the verification checklist —
 * end the agent's turn and hold the composer shut until they are answered or
 * skipped. That lock is what stops a second card being raised on top of an open
 * one, which would leave two live cards and no way to tell which reply belonged to
 * which. This is the single definition of "pending": the rail uses it to decide
 * which card is still live, and the panels use it to gate sending.
 */
import type { ChatMessage } from "@/types/chat.type";
import { parseAskUserQuestions } from "./askUserQuestion";
import { parseVerifyRequest } from "./verifyWithUser";
import { parsePlanMonthRequest } from "./planMonth";

/**
 * The message holding a card the user hasn't dealt with, or null if there is none.
 *
 * An open card is only ever the tail of the transcript: the asking turn must be the
 * newest message, because anything after it is the user's reply.
 */
export function findPendingInteraction(
  messages: Pick<ChatMessage, "id" | "role" | "toolEvents">[],
): { messageId: string } | null {
  const last = messages[messages.length - 1];
  if (!last || !holdsCard(last)) return null;
  return { messageId: last.id };
}

function holdsCard(message: Pick<ChatMessage, "role" | "toolEvents">): boolean {
  return (
    message.role === "assistant" &&
    (message.toolEvents ?? []).some(
      (event) =>
        parseAskUserQuestions(event) !== null ||
        parseVerifyRequest(event) !== null ||
        parsePlanMonthRequest(event) !== null,
    )
  );
}

/** Shared threads: the message that answered each card, keyed by the card's message id. */
export function findCardResponses<M extends Pick<ChatMessage, "respondsTo">>(
  messages: M[],
): Map<string, M> {
  const responses = new Map<string, M>();
  for (const message of messages) {
    // The server keeps only the first answer; the first one here is it.
    if (message.respondsTo && !responses.has(message.respondsTo)) {
      responses.set(message.respondsTo, message);
    }
  }
  return responses;
}

/**
 * The open card in a shared thread. Several people are there and may talk a
 * card over before anyone answers, so later messages do not close it the way
 * they do in a private thread: it stays open until someone answers it. Only
 * the newest card can be open; a newer one replaces it.
 *
 * A card from before the thread was shared was answered the private way, by
 * the next message, with nothing marking it: any later message closes it.
 */
export function findOpenSharedCard(
  messages: Pick<ChatMessage, "id" | "role" | "toolEvents" | "createdAt">[],
  responses: ReadonlyMap<string, unknown>,
  sharedAt: string | null,
): { messageId: string } | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]!;
    if (!holdsCard(message)) continue;
    if (responses.has(message.id)) return null;
    const beforeSharing = !!sharedAt && message.createdAt < sharedAt;
    if (beforeSharing && i < messages.length - 1) return null;
    return { messageId: message.id };
  }
  return null;
}
